import { useState, useEffect } from 'react';
import * as Yup from 'yup';

import {
  EXPERIENCE,
  EXPERIENCE_POSITION,
  EXPERIENCE_EMPLOYER,
  CURRENT,
  DESCRIPTION,
  START_DATE,
  END_DATE,
} from '@zr/utilities/profileFields';

import { valEndYearMonthAfterStartYearMonth } from '@zr/utilities/form/validators';

import {
  yearMonthToIso8061,
  iso8061ToYearMonth,
} from '@zr/utilities/date';

import {
  STRING_FIELD_EXPERIENCE_TITLE_REQUIRED,
  STRING_FIELD_EXPERIENCE_COMPANY_REQUIRED,
  STRING_FIELD_START_YEAR_REQUIRED,
  STRING_FIELD_EXPERIENCE_START_MONTH_REQUIRED,
  STRING_FIELD_END_YEAR_REQUIRED,
  STRING_FIELD_EXPERIENCE_END_MONTH_REQUIRED,
  STRING_FIELD_END_BEFORE_START_DATE,
} from '@zr/components/Fields/labels';

import {
  STRING_OF,
  STRING_EXPERIENCE,
} from '../labels';

import handleUpdateProfileFormikSubmit from './handleUpdateProfileFormikSubmit';

// these fields aren't profile jso fields, but are transformed into the profile date fields
export const EXPERIENCE_START_YEAR = 'start_year';
export const EXPERIENCE_START_MONTH = 'start_month';
export const EXPERIENCE_END_YEAR = 'end_year';
export const EXPERIENCE_END_MONTH = 'end_month';

// eslint-disable-next-line arrow-body-style
export const validationSchema = () => Yup.lazy((values) => {
  return Yup.object({
    [EXPERIENCE_POSITION]: Yup.string().trim().required(STRING_FIELD_EXPERIENCE_TITLE_REQUIRED),
    [EXPERIENCE_EMPLOYER]: Yup.string().trim().required(STRING_FIELD_EXPERIENCE_COMPANY_REQUIRED),
    [EXPERIENCE_START_YEAR]: Yup.string().trim().required(STRING_FIELD_START_YEAR_REQUIRED),
    [EXPERIENCE_START_MONTH]: Yup.string().trim().required(STRING_FIELD_EXPERIENCE_START_MONTH_REQUIRED),
    [EXPERIENCE_END_YEAR]: Yup.string().test(
      'end_year_required',
      STRING_FIELD_END_YEAR_REQUIRED,
      () => (values[CURRENT] || values[EXPERIENCE_END_YEAR]),
    ).test(
      'end_date_is_before_start_date_year',
      STRING_FIELD_END_BEFORE_START_DATE,
      () => (
        valEndYearMonthAfterStartYearMonth(
          values[EXPERIENCE_START_YEAR],
          values[EXPERIENCE_START_MONTH],
          values[EXPERIENCE_END_YEAR],
          values[EXPERIENCE_END_MONTH],
        )
      ),
    ),
    [EXPERIENCE_END_MONTH]: Yup.string().test(
      'end_month_required',
      STRING_FIELD_EXPERIENCE_END_MONTH_REQUIRED,
      () => (values[CURRENT] || values[EXPERIENCE_END_MONTH]),
    ).test(
      'end_date_is_before_start_date_month',
      STRING_FIELD_END_BEFORE_START_DATE,
      () => (
        valEndYearMonthAfterStartYearMonth(
          values[EXPERIENCE_START_YEAR],
          values[EXPERIENCE_START_MONTH],
          values[EXPERIENCE_END_YEAR],
          values[EXPERIENCE_END_MONTH],
        )
      ),
    ),
  });
});

// this is never prefilled (but maybe in the future if re-used)
export const initialValues = {
  [EXPERIENCE_POSITION]: '',
  [EXPERIENCE_EMPLOYER]: '',
  [CURRENT]: true,
  [EXPERIENCE_START_YEAR]: '',
  [EXPERIENCE_START_MONTH]: '',
  [EXPERIENCE_END_YEAR]: '',
  [EXPERIENCE_END_MONTH]: '',
  [DESCRIPTION]: '',
};

// for adding a 2nd entry, current is false
export const initialValuesCurrentFalse = {
  [EXPERIENCE_POSITION]: '',
  [EXPERIENCE_EMPLOYER]: '',
  [CURRENT]: false,
  [EXPERIENCE_START_YEAR]: '',
  [EXPERIENCE_START_MONTH]: '',
  [EXPERIENCE_END_YEAR]: '',
  [EXPERIENCE_END_MONTH]: '',
  [DESCRIPTION]: '',
};

/**
 * Transforms experience form values into an object structured for patching profile
 * Note this is a single entry, the actual profile API accepts arrays only, see transformExperienceFormToPatchData
 *
 * @param {object} formData experience form data
 * @returns patchEntry transformed experience object structured as an entry for patching
 */
export const transformExperienceFormToPatchEntry = (
  formData,
) => {
  // filter out only the profile jso fields
  const experienceEntry = {
    [EXPERIENCE_POSITION]: formData[EXPERIENCE_POSITION],
    [EXPERIENCE_EMPLOYER]: formData[EXPERIENCE_EMPLOYER],
    [CURRENT]: formData[CURRENT],
    [DESCRIPTION]: formData[DESCRIPTION],
  };

  // start date fields should have been required
  experienceEntry[START_DATE] = yearMonthToIso8061(
    formData[EXPERIENCE_START_YEAR],
    formData[EXPERIENCE_START_MONTH],
  );
  experienceEntry[`${START_DATE}_precision`] = 'YearMonth';

  // end date not required if current is true
  if (!formData[CURRENT]) {
    experienceEntry[END_DATE] = yearMonthToIso8061(
      formData[EXPERIENCE_END_YEAR],
      formData[EXPERIENCE_END_MONTH],
    );
    experienceEntry[`${END_DATE}_precision`] = 'YearMonth';
  }

  return experienceEntry;
};

/**
 * Transforms experience form values and adds them to an array of experiences ready for patching the profile with
 *
 * @param {object} formData experience form data
 * @param {object} previousExperienceEntries rest of jobseeker's experience required to correctly update their profile with
 * @returns {object} patchData
 */
export const transformExperienceFormToPatchData = (
  formData, previousExperienceEntries = { [EXPERIENCE]: [] },
) => {
  const experienceEntry = transformExperienceFormToPatchEntry(formData);

  return {
    [EXPERIENCE]: [
      ...previousExperienceEntries[EXPERIENCE],
      experienceEntry,
    ],
  };
};

/**
 * Transforms server profile jso structured experience entry into experience form values
 *
 * @param {object} serverEntry single experience entry structured from the server
 * @returns formValues
 */
export function transformExperienceServerEntryToFormData(serverEntry) {
  const startDate = iso8061ToYearMonth(serverEntry[START_DATE]);
  const endDate = iso8061ToYearMonth(serverEntry[END_DATE]);

  const formData = {
    // API can return fields as null or empty string, or leave the field absent
    // handle them all as empty string
    [EXPERIENCE_POSITION]: serverEntry[EXPERIENCE_POSITION] || '',
    [EXPERIENCE_EMPLOYER]: serverEntry[EXPERIENCE_EMPLOYER] || '',
    [CURRENT]: serverEntry[CURRENT],
    [EXPERIENCE_START_YEAR]: startDate.year,
    [EXPERIENCE_START_MONTH]: startDate.month,
    [EXPERIENCE_END_YEAR]: endDate.year,
    [EXPERIENCE_END_MONTH]: endDate.month,
    [DESCRIPTION]: serverEntry[DESCRIPTION] || '',
    // server index
    _idx: serverEntry.idx,
  };

  return formData;
}

/**
 * Formik submit handler for updating experience and handles add another/no more entries logic
 * @param {object} addAnotherRef react ref containing the add another/no more entries flag
 * @param {string} options.method used in profile_method
 * @param {function} options.onAddAnother callback ran when successfully updated with add another specified
 * @param {function} options.onNoMore callback ran when successfully updated with no more entries specified
 * @param {function} options.onError callback handling all errors besides validation_error
 * @param {function} options.onStartSubmit callback ran at the start, useful for clearing previous errors
 * @param {function} options.onValidationError callback ran when running into a validation_error
 * @returns formik submit handler
 */
export const useExperienceFormikSubmit = (
  addAnotherRef,
  setIsLoading = () => {},
  {
    method,
    onAddAnother = () => {},
    onNoMore,
    onError,
    onStartSubmit,
    onValidationError = () => {},
    previousExperiences = [],
  },
) => {
  const [experiences, setExperiences] = useState({
    [EXPERIENCE]: previousExperiences,
  });

  useEffect(() => {
    setExperiences({
      [EXPERIENCE]: previousExperiences,
    });
  // only expect previousExperiences to change by adding or removing experiences
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousExperiences.length]);

  const handleFormSubmit = async (data, formik) => {
    const nextExperiences = transformExperienceFormToPatchData(data, experiences);

    const handleFormSuccess = async () => {
      const lastExperienceAdded = transformExperienceFormToPatchEntry(data);
      if (addAnotherRef.current) {
        const { resetForm } = formik;
        setExperiences(nextExperiences);
        // reset the form
        resetForm({
          values: initialValuesCurrentFalse,
        });
        onAddAnother(lastExperienceAdded);
      } else {
        // callback could be async (might call apply endpoint), await keeps isSubmitting true during that
        await onNoMore(lastExperienceAdded);
      }
    };

    // when the profile api returns a validation error for experience, get the actual entry we're validating
    const transformValidationFieldsStatus = (fields) => {
      // assume we're validating the last entry, may not be true if we use this more
      const index = nextExperiences[EXPERIENCE].length - 1;
      const invalidEntry = fields[EXPERIENCE]?.find(({ _idx }) => (
        _idx === index
      ));
      return invalidEntry || fields;
    };

    // await needed to make formik isSubmitting to stay true while this is going on
    await handleUpdateProfileFormikSubmit({
      method,
      onError,
      onStart: onStartSubmit,
      onSuccess: handleFormSuccess,
      onValidationError,
      transformValidationFieldsStatus,
      setIsLoading,
    })(nextExperiences, formik);
  };

  const numExperiencesAdded = experiences[EXPERIENCE].length - previousExperiences.length;

  return [numExperiencesAdded, handleFormSubmit];
};

export function getLegendText(entryCount) {
  // if submitted an entry, start showing the count
  if (entryCount) {
    const entryDisplay = `${entryCount + 1} ${STRING_OF} ${entryCount + 1}`;
    return `${STRING_EXPERIENCE} ${entryDisplay}`;
  }
  return STRING_EXPERIENCE;
}

/**
 * Crafts a validate_only experience formik submit handler
 *
 * @param {object} options.formData experience form data
 * @param {object} options.formikProps formik props provided by formik
 * @param {string} options.method used in profile_method
 * @param {function} options.onError callback handling all errors besides validation_error
 * @param {function} options.onStartSubmit callback ran at the start, useful for clearing previous errors
 * @param {function} options.onSuccess callback ran when successfully validated
 * @param {function} options.onValidationError callback ran when running into a validation_error
 * @returns {function} formik submit handler
 */
export async function experienceFormikValidateOnlySubmit({
  formData,
  formikProps,
  method,
  onError,
  onStartSubmit,
  onSuccess,
  onValidationError,
}) {
  // when the profile api returns a validation error for experience, get the actual entry we're validating
  const transformValidationFieldsStatus = (fields) => (
    fields[EXPERIENCE]?.[0] ?? fields
  );

  await handleUpdateProfileFormikSubmit({
    method,
    onError,
    onStart: onStartSubmit,
    onSuccess,
    onValidationError,
    transformValidationFieldsStatus,
    validateOnly: true,
  })(transformExperienceFormToPatchData(formData), formikProps);
}
